import {useCookies} from "../composables/useCookies";
import Model from "./Model.ts";
import AppPermission from "./AppPermission.ts";
import api from "../api.ts";

/**
 * Represents a category in the application.
 * Categories group permissions logically.
 *
 * @author Jackson Bingham <jackson@onlineimage.com>
 * @date November 2024
 */
class AppCategory extends Model {
    /** The title or name of the category. */
    private _title: string;

    /** The list of permissions associated with this category, if applicable.
     * @readonly This field is an optional property returned and cannot be modified.
     */
    private readonly _permissions: AppPermission[] | undefined;

    /**
     * Creates a new category by sending a POST request to the server.
     *
     * @param client_id The client ID of the application the new category will belong to.
     * @param values - The form data to create a new category.
     * @returns A Promise that resolves to the newly created `AppCategory`.
     * @example
     * ```ts
     * const newCategory = await AppCategory.create({ title: "Accounts" });
     * console.log(newCategory.title); // "Accounts"
     * ```
     */
    public static async create(client_id: string, values: ICategoryForm): Promise<[boolean, AppCategory, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(
                "/api/oix/roles/categories",
                values, // Axios automatically stringifies JSON objects
                {
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        client_id
                    }
                }
            );

            const category: AppCategory = AppCategory.fromJson(response.data);
            return [true, category];
        } catch (error) {
            console.error(error);
            return [false, null, error];
        }
    }

    /**
     * Retrieves all categories from the server.
     *
     * @param client_id - The client ID of the application the categories belong to.
     * @param with_permissions - Whether to include permissions with each category. Defaults to `"n"`.
     * @returns A Promise that resolves to a list of `AppCategory` instances.
     * @example
     * ```ts
     * const categories = await AppCategory.getAll("client123", "y");
     * console.log(categories.length); // 5
     * ```
     */
    public static async getAll(client_id: string, with_permissions: "y" | "n" = "n"): Promise<AppCategory[]> {
        const tokenCookie = useCookies().getCookie("access_token");

        const response = await api.get("/api/oix/roles/categories", {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${tokenCookie}`,
            },
            params: {
                client_id, // Axios handles query string construction
                with_permissions,
            },
        });

        return response.data.categories.map((category: any) => AppCategory.fromJson(category));
    }

    /**
     * Creates a new AppCategory instance from a JSON object.
     * @param json - The JSON object representing the category.
     * @returns A new `AppCategory` instance.
     * @example
     * const json = { id: "101", title: "Accounts" };
     * const category = AppCategory.fromJson(json);
     * console.log(category.id); // "101"
     */
    public static fromJson(json: any): AppCategory {
        return new AppCategory(json.id, json.title, json?.permissions);
    }

    /**
     * Constructs a new AppCategory instance.
     * @param id - The unique identifier for the category.
     * @param title - The title or name of the category.
     * @param permissions - (Optional) the permissions assigned to this category.
     * @example
     * const category = new AppCategory("101", "Accounts");
     * console.log(category.title); // "Accounts"
     */
    constructor(id: string, title: string, permissions?: any[]) {
        super(id);
        this._title = title;
        this._permissions = permissions?.map(
            (permission) => AppPermission.fromJson(permission)
        );
    }

    /**
     * Gets the title of the category.
     *
     * @returns The category title.
     * @example
     * ```ts
     * const category = new AppCategory("101", "Accounts");
     * console.log(category.title); // "Accounts"
     * ```
     */
    public get title(): string {
        return this._title;
    }

    /**
     * Gets the permissions associated with the category.
     *
     * @returns An array of `AppPermission` objects, or `undefined` if no permissions are associated.
     * @example
     * ```ts
     * const category = new AppCategory("101", "Accounts", ...);
     * // Assuming permissions are created...
     * console.log(category.permissions?.length); // 1
     * ```
     */
    public get permissions(): AppPermission[] | undefined {
        return this._permissions;
    }

    /**
     * Converts the `AppCategory` instance to a plain JSON object.
     *
     * This method overrides the base `toJSON` implementation to include the `title` and `permissions` fields.
     *
     * @override
     * @returns An object containing the `AppCategory`'s `id`, `title`, and associated permissions.
     *
     * @example
     * ```ts
     * const category = new AppCategory("123", "Accounts", [{ id: "101", title: "create_account" }]);
     * console.log(category.toJSON());
     * // { id: "123", title: "Accounts", permissions: [...] }
     * ```
     */
    public override toJSON() {
        return {
            id: this.id,
            title: this._title,
            permissions: this._permissions,
        }
    }

    /**
     * Updates the current category by sending a PATCH request to the server.
     *
     * This method sends the updated category data along with the client ID to the server,
     * updates the current instance with the response, and returns the updated category.
     *
     * @param client_id - The client ID of the application the category belongs to.
     * @param values - The form data containing the updated category information.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the update was successful (`true`) or not (`false`).
     * - The second element is the updated `AppCategory` instance if the update was successful, or `null` if it failed.
     *
     * @example
     * ```ts
     * const category = new AppCategory("101", "Accounts");
     * const [updated, updatedCategory] = await category.update("client123", { title: "New Accounts" });
     * console.log(updated); // true
     * console.log(updatedCategory?.title); // "New Accounts"
     * ```
     */
    public async update(client_id: string, values: ICategoryForm): Promise<[boolean, AppCategory, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.patch(
                `/api/oix/roles/categories/${this.id}`,
                values, // Axios automatically stringifies JSON objects
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        client_id,
                    }
                },
            );

            // Update local instance with the response data
            this._title = response.data.category.title;
            return [true, this];
        } catch (error) {
            console.error(error);
            return [false, null, error];
        }
    }

    /**
     * Deletes the current category by sending a DELETE request to the server.
     *
     * @param client_id - The client ID of the application the role belongs to.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the deletion was successful (`true`) or not (`false`).
     * - The second element is an `Error` object if the deletion failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const category = new AppCategory("101", "Accounts");
     * const [success, error] = await category.destroy("client123");
     *
     * if (success) {
     *   console.log("Category successfully deleted");
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async destroy(client_id: string): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            // Send DELETE request to the server
            await api.delete(`/api/oix/roles/categories/${this.id}`, {
                headers: {
                    Authorization: `Bearer ${tokenCookie}`,
                },
                params: {
                    client_id,
                },
            });

            return [true];
        } catch (error) {
            console.error("Error deleting category:", error);
            return [false, error];
        }
    }
}

export default AppCategory;

/**
 * Interface representing the structure of the form used to create or update a category.
 */
export interface ICategoryForm {
    /** The title or name of the category. */
    title: string;
}
